@if (loading()) {
  <!-- While a hub loads (a few hundred ms after an in-app navigation) a neutral skeleton holds the
       hero. It replaced a blank page, and on hub-to-hub links the previous hub's content. -->
  <section class="sav-silo-page" aria-busy="true" aria-label="Loading topic">
    <div class="sav-silo-container sav-hub-skel">
      <span class="sav-skel sav-skel-badge"></span>
      <span class="sav-skel sav-skel-h1"></span>
      <span class="sav-skel sav-skel-line"></span>
      <span class="sav-skel sav-skel-line sav-skel-line-short"></span>
    </div>
  </section>
}
@if (silo(); as silo) {
  <section class="sav-silo-page">
    <div class="sav-silo-container">
      <app-breadcrumb [items]="crumbs()" />
    </div>

    <section class="sav-winter-redesign-section">
      <div class="sav-silo-container sav-winter-redesign-grid">
        <div class="sav-winter-redesign-content">
          @if (hasName()) {
            <span class="sav-winter-redesign-badge">{{ silo.name }}</span>
          }

          @if (hasHeading()) {
            <h1>
              {{ headingParts().before }}
              @if (headingParts().last) {
                <span>{{ headingParts().last }}</span>
              }
            </h1>
          }

          @if (hasShortContent()) {
            <div
              class="sav-winter-redesign-cms"
              [innerHTML]="silo.shortContent | trustedCmsHtml"
            ></div>

            <!-- Both used to be href="#": with <base href="/"> that loaded the home page. -->
            @if (dealsLink() || blogs().length > 0) {
              <div class="sav-winter-redesign-actions">
                @if (dealsLink(); as deals) {
                  @if (deals.path) {
                    <a
                      [routerLink]="deals.path"
                      class="sav-winter-redesign-btn sav-winter-redesign-btn-primary"
                      >{{ deals.label }}</a
                    >
                  } @else if (deals.section) {
                    <a
                      [attr.href]="'/blogs/' + silo.slug + '#' + deals.section"
                      (click)="scrollToSection($event, deals.section)"
                      class="sav-winter-redesign-btn sav-winter-redesign-btn-primary"
                      >{{ deals.label }}</a
                    >
                  }
                }
                @if (blogs().length > 0) {
                  <a
                    [attr.href]="'/blogs/' + silo.slug + '#hub-guides'"
                    (click)="scrollToSection($event, 'hub-guides')"
                    class="sav-winter-redesign-btn sav-winter-redesign-btn-secondary"
                    >Browse the Guides</a
                  >
                }
              </div>
            }
          }

          @if (heroFeatures().length > 0) {
            <ul class="sav-winter-redesign-highlights">
              @for (feature of heroFeatures(); track feature) {
                <li>
                  <span class="sav-winter-redesign-check" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 13L9 17L19 7"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  {{ feature }}
                </li>
              }
            </ul>
          }
        </div>

        @if (heroProducts().length > 0 && currentSlide(); as slide) {
          <div class="sav-winter-redesign-panel">
            <div class="sav-winter-redesign-card">
              <div class="sav-winter-redesign-card-head">
                <h2>Featured This Week</h2>
                @if (heroProducts().length > 1) {
                  <div class="sav-winter-redesign-controls">
                    <button type="button" aria-label="Previous slide" (click)="changeSlide(-1)">
                      <svg viewBox="0 0 24 24" fill="none">
                        <path
                          d="M15 19L8 12L15 5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      aria-label="Next slide"
                      class="is-primary"
                      (click)="changeSlide(1)"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 5L16 12L9 19"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                }
              </div>

              <div class="sav-winter-redesign-carousel" [class.is-fading]="isCarouselFading()">
                @if (slide.imageUrl) {
                  <div class="sav-winter-redesign-image-wrap">
                    <img [src]="slide.imageUrl" [alt]="slide.title" />
                  </div>
                }

                <div class="sav-winter-redesign-copy">
                  <h3>{{ slide.title }}</h3>
                  @if (slide.description) {
                    <p>{{ slide.description }}</p>
                  }

                  @if (slide.newPrice || slide.oldPrice) {
                    <div class="sav-winter-redesign-deal-actions">
                      <div class="sav-winter-redesign-price-row">
                        @if (slide.newPrice) {
                          <span class="sav-winter-redesign-new-price">{{ slide.newPrice }}</span>
                        }
                        @if (slide.oldPrice) {
                          <span class="sav-winter-redesign-old-price">{{ slide.oldPrice }}</span>
                        }
                      </div>

                      @if (slide.slug) {
                        <a
                          [routerLink]="['/', slide.slug]"
                          class="sav-winter-redesign-grab-btn"
                          >Grab Deal</a
                        >
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </section>

    @if (stores().length > 0) {
      <section id="hub-stores" class="sav-brand-directory-section sav-hub-anchor" aria-label="Silo stores">
        <div class="sav-brand-directory-wrap">
          @if (hasName()) {
            <app-section-heading
              [heading]="'Featured Stores for ' + silo.name"
              [subtitle]="'Verified partners with active offers across ' + silo.name + '.'"
            />
          }

          <div class="sav-brand-directory-grid">
            @for (store of stores(); track store.id) {
              <article class="sav-brand-card">
                <a
                  class="sav-brand-card-link"
                  [routerLink]="['/', store.slug]"
                  [attr.aria-label]="store.name"
                >
                  <div
                    class="sav-brand-cover"
                    [style.background-color]="store.backgroundColor"
                    [style.background-image]="store.coverUrl ? 'url(' + store.coverUrl + ')' : null"
                  ></div>
                  @if (store.logoUrl) {
                    <img [src]="store.logoUrl" [alt]="store.name" class="sav-brand-avatar" />
                  }
                  <h3>{{ store.name }}</h3>
                  @if (store.domain) {
                    <p>@{{ store.domain }}</p>
                  }
                </a>
                @if (store.description) {
                  <p class="sav-brand-description">{{ store.description }}</p>
                }
              </article>
            }
          </div>
        </div>
      </section>
    }

    <section class="sav-silo-section-block">
      <div class="sav-silo-container">
        @if (products().length > 0) {
          <section class="sav-essentials" aria-label="Silo products">
            <div class="sav-event-essentials-wrap">
              @if (hasName()) {
                <app-section-heading
                  [heading]="silo.name + ' Essentials'"
                  subtitle="A curated collection of products worth watching in this topic."
                />
              }

              <div class="sav-product-grid sav-full-rows">
                @for (product of products(); track product.id) {
                  <app-product-card
                    [title]="product.title"
                    [image]="product.imageUrl"
                    [hoverImage]="product.hoverImageUrl"
                    [storeName]="product.storeName"
                    [price]="product.newPrice"
                    [oldPrice]="product.oldPrice"
                    [href]="product.slug ? '/' + product.slug : ''"
                  />
                }
              </div>
            </div>
          </section>
        }

        @if (blogs().length > 0) {
          <!-- No inner container: the parent `.sav-silo-container` already applies the site
               gutter, and nesting a second one indented this whole block. -->
          <section id="hub-guides" class="sav-hub-anchor mb-[50px]">
            <div>
              @if (hasName()) {
                <app-section-heading
                  heading="Guides, Coupon Tips and Deal Updates"
                  [subtitle]="
                    'Expert savings advice, verified coupon strategies and related shopping insights for ' +
                    silo.name +
                    '.'
                  "
                />
              }

              <!-- Shared blog card + grid: 4 per row, no sidebar on this page. `sav-full-rows`
                   trims a part-filled last row, so the section never ends on one or two stray
                   cards — see the note on that class in styles.scss. -->
              <div class="sav-blog-grid sav-full-rows mb-20">
                @for (blog of blogs(); track blog.id) {
                  <app-blog-post-card
                    [post]="{
                      slug: blog.slug,
                      title: blog.name,
                      excerpt: blog.shortDescription,
                      imageUrl: blog.imageUrl,
                      category: blog.categoryName,
                      readTime: blog.readTime,
                    }"
                  />
                }
              </div>
            </div>
          </section>
        }

        @if (relevantSilos().length > 0) {
          <section class="mt-32 pb-32">
            <app-section-heading
              heading="Explore Related Topics"
              [subtitle]="'More savings guides and topic hubs connected to ' + silo.name + '.'"
            />

            <!-- Light cards matching the rest of the site. The previous dark, square tiles left
                 ~60% of each card empty, repeated the CURRENT hub's name on every card (identical
                 five times over, so it labelled nothing), and hid the guide count behind hover. -->
            <div class="sav-topic-grid">
              @for (relevantSilo of relevantSilos(); track relevantSilo.id) {
                <a class="sav-topic-card" [routerLink]="['/blogs', relevantSilo.slug]">
                  <span class="sav-topic-card-name">{{ relevantSilo.name }}</span>

                  @if (relevantSilo.shortContent) {
                    <span class="sav-topic-card-desc">{{ relevantSilo.shortContent }}</span>
                  }

                  <span class="sav-topic-card-foot">
                    @if (relevantSilo.totalBlogs !== null) {
                      <span class="sav-topic-card-count">
                        <strong>{{ relevantSilo.totalBlogs }}</strong>
                        {{ relevantSilo.totalBlogs === 1 ? 'guide' : 'guides' }}
                      </span>
                    }
                    <span class="sav-topic-card-cue" aria-hidden="true">Explore →</span>
                  </span>
                </a>
              }
            </div>
          </section>
        }

        @if (hasLongContent()) {
          <section class="sav-longform" aria-label="Complete silo guide">
            @if (hasName()) {
              <app-section-heading
                [heading]="'The Complete ' + silo.name"
                subtitle="Everything worth knowing about saving in this topic, in one place."
              />
            }

            <div
              class="sav-longform-preview sav-longform-content"
              [innerHTML]="silo.longContent | trustedCmsHtml"
            ></div>
          </section>
        }
      </div>
    </section>
  </section>
}

